import type { OrderLine, SalesOrder } from '../../../domain/types'
import type { Order } from '@spcc/domains/orders'

function containsArabicScript(value: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(value)
}

export function calculateUtilization(salesOrder: SalesOrder): number {
  const totalQty = salesOrder.lines.reduce((sum, l) => sum + l.allocatedQuantity.value, 0)
  if (totalQty === 0) return 0
  const usedQty = salesOrder.lines.reduce((sum, l) => sum + l.usedQuantity.value, 0)
  return Math.min(100, Math.round((usedQty / totalQty) * 100))
}

export function getOrdersCount(
  salesOrderId: string,
  orders: Array<Pick<Order, 'contractId' | 'status'>>,
  extraSalesOrderIds: Array<string | number | null | undefined> = []
): { total: number; pending: number; delivered: number } {
  const ids = [salesOrderId, ...extraSalesOrderIds]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)

  if (ids.length === 0 || orders.length === 0) {
    return { total: 0, pending: 0, delivered: 0 }
  }

  let total = 0
  let pending = 0
  let delivered = 0

  for (const order of orders) {
    const contractId = String(order.contractId ?? '').trim()
    if (!contractId || !ids.includes(contractId)) continue

    total += 1
    if (order.status === 'pending' || order.status === 'processing') {
      pending += 1
    }
    if (order.status === 'delivered') {
      delivered += 1
    }
  }

  return { total, pending, delivered }
}

export function formatCurrency(amount: number): string {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`
  return amount.toString()
}

export function getSalesOrderLineLabel(
  line: Pick<OrderLine, 'variantName' | 'product' | 'productCode'> | null | undefined,
  isArabic: boolean,
): string {
  const variantName = String(line?.variantName ?? '').trim()
  if (variantName) {
    if (isArabic || !containsArabicScript(variantName)) {
      return variantName
    }
  }

  const product = line?.product
  const localizedName =
    typeof product === 'string'
      ? product
      : isArabic
        ? product?.ar
        : product?.en
  const name = String(localizedName ?? '').trim()
  if (name) return name

  const fallbackName =
    typeof product === 'object'
      ? String(product?.en || product?.ar || '').trim()
      : ''
  if (fallbackName) return fallbackName

  if (variantName) return variantName

  return String(line?.productCode ?? '').trim()
}
